import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Linkedin, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import FunnelLayout from "@/v2/components/FunnelLayout";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import federicoImage from "@/assets/federico-piccinini.png.asset.json";

const AboutContatti = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Campi mancanti",
        description: "Compila tutti i campi per inviare il messaggio",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: formData.name,
          email: formData.email,
          message: formData.message,
        },
      });

      if (error) throw error;

      toast({
        title: "Messaggio Inviato!",
        description: "Ti abbiamo inviato una conferma via email. Ti risponderemo entro 24 ore.",
      });

      setFormData({ name: "", email: "", message: "" });
    } catch (error: unknown) {
      console.error("Error sending message:", error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore. Riprova più tardi.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FunnelLayout>
      <Helmet>
        <title>Chi sono | Federico Piccinini - Progetto Startup</title>
        <meta name="description" content="Aiuto founder italiani a testare idee reali senza sprecare mesi a costruire il prodotto sbagliato." />
        <link rel="canonical" href="https://progettostartup.com/about" />
        <meta property="og:url" content="https://progettostartup.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Chi sono - Progetto Startup" />
        <meta name="twitter:description" content="Aiuto founder italiani a testare idee reali." />
        <meta property="og:title" content="Chi sono - Progetto Startup" />
        <meta property="og:description" content="Aiuto founder italiani a testare idee reali." />
      </Helmet>

      {/* Hero */}
      <section className="pt-6 pb-12 md:pb-16">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4">
            Chi sono
          </h1>
          <p className="text-lg md:text-xl text-slate-300">
            Aiuto founder italiani a testare idee reali senza sprecare mesi a costruire il prodotto sbagliato.
          </p>
        </div>
      </section>

      {/* Federico */}
      <section className="border-t border-white/10 py-12 md:py-16">
        <div className="max-w-2xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border border-white/10 mb-6">
              <img
                src={federicoImage.url}
                alt="Federico Piccinini"
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-100 mb-1">
              Federico Piccinini
            </h2>
            <p className="text-base text-slate-400 mb-6">Founder, Progetto Startup</p>

            <div className="space-y-4 text-slate-300 mb-8">
              <p>
                Ho affiancato più di 300 founder nella fase più difficile: quella in cui l'idea c'è, ma non si sa se ha senso.
              </p>
              <p>
                Ho costruito Proof perché il problema non è avere idee è capire subito quali meritano il tuo tempo.
              </p>
              <p>
                Lavoro su AI, no-code e validation. Se hai un'idea e vuoi sapere se regge, sei nel posto giusto.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                asChild
                className="border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08] hover:text-white"
              >
                <a href="https://www.linkedin.com/in/federico-piccinini" target="_blank" rel="noopener noreferrer">
                  <Linkedin className="w-4 h-4 mr-2" />
                  LinkedIn
                </a>
              </Button>
              <Button
                variant="outline"
                size="sm"
                asChild
                className="border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08] hover:text-white"
              >
                <a href="mailto:ad.piccinini.federico@gmail.com">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="border-t border-white/10 py-12 md:py-16">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-100 mb-3">
            Scrivimi
          </h2>
          <p className="text-lg text-slate-300">
            Hai domande su Proof o vuoi raccontarmi la tua idea? Ti rispondo entro 24 ore.
          </p>
        </div>

        <div className="max-w-lg mx-auto">
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-slate-300">Nome</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Il tuo nome"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tua@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-300">Messaggio</Label>
                <Textarea
                  id="message"
                  placeholder="Raccontami del tuo progetto o delle tue domande..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  required
                  className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-violet-600 hover:bg-violet-500 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Invio..." : "Invia"}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>
    </FunnelLayout>
  );
};

export default AboutContatti;
